import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Button, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeStyles } from "tss-react/mui";
import Image from "next/image";
import { AppLinks, AppSize, pxToRem } from "@/util";
import { AppColors } from "@/assets/colors";
import Link from "next/link";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import AppDrawer from "../home/drawer";
import MobileNav from "../home/mobileNav";

const pages = [
  { name: "Home", path: AppLinks.PUBLIC.HOME },
  { name: "Project", path: AppLinks.PUBLIC.PROJECTS },
  { name: "Services", path: AppLinks.PUBLIC.SERVICES },
  { name: "About us", path: AppLinks.PUBLIC.ABOUT },
  { name: "Contact us", path: AppLinks.PUBLIC.CONTACT },
];

const projectLink: { name: string; link: string }[] = [
  { name: "Portfolio", link: AppLinks.PUBLIC.PORTFOLIOS },
  { name: "Our Listings", link: AppLinks.PUBLIC.LISTINGS },
];

function Nav() {
  const [anchorProjectNav, setAnchorProjectNav] =
    React.useState<null | HTMLElement>(null);
  const [mobileNav, setMobileNav] = React.useState<boolean>(false);

  const handleOpenProject = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorProjectNav(event.currentTarget);
  };

  const handleCloseProject = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorProjectNav(null);
  };

  const theme = useTheme();
  const { classes } = styles();

  return (
    <AppBar position="fixed" className={classes.nav}>
      <Toolbar disableGutters className={classes.container}>
        <Box className={classes.sub}>
          <Link href={AppLinks.PUBLIC.HOME} passHref>
            <Button>
              <Typography color="textPrimary" className={classes.navTxt}>
                <Image
                  src="/images/nav/Logo.svg"
                  width={50}
                  height={84}
                  alt="Logo goes here"
                />
              </Typography>
            </Button>
          </Link>

          <Box sx={{ gap: 4, display: { xs: "none", md: "flex" } }}>
            {pages.map((page) =>
              page.name === "Project" ? (
                <Button
                  key={page.name}
                  onClick={handleOpenProject}
                  endIcon={
                    <ExpandMoreIcon
                      fontSize="large"
                      style={{ color: theme.colors.text }}
                    />
                  }
                  sx={{ my: 2, display: "flex" }}
                >
                  <Typography color="textPrimary" className={classes.navTxt}>
                    {page.name}
                  </Typography>
                </Button>
              ) : (
                <Link key={page.name} href={page.path} passHref>
                  <Button
                    sx={{ my: 2, display: "block", padding: theme.spacing(2) }}
                  >
                    <Typography color="textPrimary" className={classes.navTxt}>
                      {page.name}
                    </Typography>
                  </Button>
                </Link>
              )
            )}
          </Box>

          <Menu
            id="menu-appbar"
            anchorEl={anchorProjectNav}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            disableScrollLock={true}
            open={Boolean(anchorProjectNav)}
            onClose={handleCloseProject}
            sx={{ display: { md: "block" } }}
          >
            {projectLink.map((item) => (
              <Link key={item.name} href={item.link} passHref>
                <MenuItem>
                  <Typography className={classes.projectLinkTxt}>
                    {item.name}
                  </Typography>
                </MenuItem>
              </Link>
            ))}
          </Menu>
        </Box>

        <Box className={classes.actBtn}>
          <Box sx={{ display: { md: "flex" }, gap: 2 }}>
            <Button
              className={classes.consultBtn}
              href={AppLinks.PUBLIC.CONSULTATION}
              endIcon={
                <Image
                  src={"/images/nav/calendar-add.svg"}
                  width={20}
                  height={20}
                  alt="calendar"
                />
              }
            >
              <Typography className={classes.consultTxt}>
                Book Consultation
              </Typography>
            </Button>
          </Box>
        </Box>

        <AppDrawer open={mobileNav}>
          <MobileNav handleClose={() => setMobileNav(!mobileNav)} />
        </AppDrawer>
        <IconButton
          className={classes.menu}
          onClick={() => setMobileNav(!mobileNav)}
        >
          <MenuRoundedIcon className={classes.menuicon} fontSize="large" />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
}

export default Nav;

const styles = makeStyles()((theme) => ({
  nav: {
    background: theme.colors.white,
    padding: `0px ${pxToRem(AppSize.padding)}`,
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.down("sm")]: {
      padding: `0px ${pxToRem(20)}`,
    },
  },
  menu: {
    display: "none",
    [theme.breakpoints.down(966)]: {
      display: "flex",
      padding: 12,
      width: 56,
      height: 56,
    },
  },
  menuicon: {
    fontSize: "2.5rem",
    color: AppColors.primary["600"],
  },
  actBtn: {
    display: "flex",
    flexGrow: 0,
    [theme.breakpoints.down(966)]: {
      display: "none",
    },
  },
  container: {
    gap: theme.spacing(4),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  navTxt: {
    fontSize: pxToRem(16),
    fontWeight: 500,
    [theme.breakpoints.down(1360)]: {
      fontSize: pxToRem(14),
    },
  },
  sub: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(4),
    alignItems: "center",
  },
  projectLinkTxt: {
    fontWeight: 500,
    fontSize: pxToRem(14),
  },
  investTxt: {
    fontWeight: 500,
    fontSize: pxToRem(16),
    [theme.breakpoints.down(1360)]: {
      fontSize: pxToRem(14),
    },
  },
  investBtn: {
    border: `1px solid ${AppColors.neutral["70"]}`,
    height: pxToRem(46),
    borderRadius: pxToRem(8),
    [theme.breakpoints.down("lg")]: {
      display: "none",
    },
  },
  consultBtn: {
    background: AppColors.gradient.secondary,
    borderRadius: pxToRem(8),
    height: pxToRem(46),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down(1360)]: {
      fontSize: pxToRem(14),
    },
  },
  consultTxt: {
    fontWeight: 500,
    fontSize: pxToRem(16),
    color: theme.colors.white,
    [theme.breakpoints.down(1360)]: {
      fontSize: pxToRem(14),
      padding: theme.spacing(0),
    },
  },
}));
